import type { HailReportTypeRules } from '@xweather/protect-types'
import { useEffect, useEffectEvent } from 'react'
import { useFormContext } from 'react-hook-form'
import { Alert } from '../../../../components/generic.tsx'
import { I18n } from '../../../../components/I18n.tsx'
import { useCreateAnalyzeReport } from '../../../../data/analyze/reports.ts'
import { useHailDataAvailability, useHailReportTypeRules } from '../../../../data/analyze/reportTypeRules.ts'
import { useFormatDate } from '../../../preferences/useFormatDate.tsx'
import { defineReportType, type ReportFormProps } from '../CreateReportForm.tsx'
import { CustomPointField } from '../fields/CustomPointField.tsx'
import { FieldErrorAlert } from '../fields/FieldErrorAlert.tsx'
import { LocationList } from '../fields/LocationList.tsx'
import { NameField } from '../fields/NameField.tsx'
import { RadiusField } from '../fields/RadiusField.tsx'
import { StartEndDateTimeFields } from '../fields/StartEndDateTimeFields.tsx'
import { TimezoneField } from '../fields/TimezoneField.tsx'
import { UpdateGeometryWhenDependentFieldsChange } from '../fields/UpdateGeometryWhenDependentFieldsChange.tsx'
import { coverageZoneOf, maxAreaKm2, maxRadiusKmForArea } from '../reportTypeRules.ts'
import { isNonEmpty } from '../schema.ts'
import { useDefaultReportName } from '../useDefaultReportName.ts'
import { HailCoverageLayer } from './map/HailCoverageLayer.tsx'
import { type HailReportForm, inWholeMinutes, useHailDefaultValues, useHailFormSchema, useHailFormValues } from './schema.ts'

export const useHailReportType = (props: ReportFormProps) => {
	const rules = useHailReportTypeRules()
	const schema = useHailFormSchema(rules)
	const defaultReportName = useDefaultReportName()
	const defaultValues = useHailDefaultValues(props.selectedLocations)
	const createReport = useCreateAnalyzeReport()

	// Hail rules are missing only while analyzeHailReports is off, so remove this check when the flag is removed
	if (!rules) return undefined
	const reportLocations = rules.maxAssets > 1 ? props.selectedLocations : props.selectedLocations.slice(0, 1)

	return defineReportType<HailReportForm>({
		title: ($) => $.reports.hail.form.newHailAnalysisReport,
		schema,
		defaultValues,
		onSubmit: ({ assetVrns, ...report }) => {
			if (!isNonEmpty(assetVrns)) throw new Error('A report needs an asset')
			return createReport.mutateAsync({ ...report, assetVrns, reportType: 'HAIL' })
		},
		mapLayers: <HailCoverageLayer rules={rules} />,
		maxAssets: rules.maxAssets,
		steps: [
			{
				id: 'areaOfInterest',
				label: <I18n i18nKey={($) => $.reports.form.steps.areaOfInterest.label} />,
				shortDescription: <I18n i18nKey={($) => $.reports.form.steps.areaOfInterest.shortDescription} />,
				longDescription: <I18n i18nKey={($) => $.reports.form.steps.areaOfInterest.longDescription} />,
				mapInteractive: true,
				form: (
					<>
						<LocationList {...props} allowUnprotectedAssets={rules.allowUnprotectedAssets} maxAssets={rules.maxAssets} />
						<RadiusField
							selectedLocations={reportLocations}
							maxRadiusKm={maxRadiusKmForArea(maxAreaKm2(rules))}
							maxAssets={rules.maxAssets}
						/>
						<FieldErrorAlert name='geometry' />
						<CustomPointField
							selectedLocation={reportLocations.length === 1 ? reportLocations[0] : undefined}
							maxAssets={rules.maxAssets}
						/>
						<UpdateGeometryWhenDependentFieldsChange maxVertexCount={rules.maxVertexCount} />
					</>
				),
				fieldsToValidate: ['assetVrns', 'customPoint', 'geometry', 'geometryTooComplex', 'geometryPending', 'radiusKm'],
			},
			{
				id: 'timeRange',
				label: <I18n i18nKey={($) => $.reports.form.steps.timeRange.label} />,
				shortDescription: <I18n i18nKey={($) => $.reports.form.steps.timeRange.shortDescription} />,
				longDescription: <I18n i18nKey={($) => $.reports.form.steps.timeRange.longDescription} />,
				form: (
					<>
						<TimezoneField />
						<HailTimeRangeFields rules={rules} />
					</>
				),
				fieldsToValidate: ['preferences.timezone', 'timeRange'],
			},
			{
				id: 'reportDetails',
				label: <I18n i18nKey={($) => $.reports.form.steps.reportDetails.label} />,
				shortDescription: <I18n i18nKey={($) => $.reports.form.steps.reportDetails.shortDescription} />,
				longDescription: <I18n i18nKey={($) => $.reports.form.steps.reportDetails.longDescription} />,
				form: (
					<NameField
						defaultName={({ radiusKm, preferences, timeRange }: HailReportForm) =>
							defaultReportName({
								locationNames: props.selectedLocations.map((location) => location.name),
								radiusKm,
								timezone: preferences.timezone,
								timeRange,
							})
						}
					/>
				),
				fieldsToValidate: ['name'],
			},
		],
	})
}

const useCoverageZoneDataAvailability = (rules: HailReportTypeRules) => {
	const [geometry] = useHailFormValues(['geometry'])
	const { data: hailDataAvailability, isPending, isError } = useHailDataAvailability()
	const zone = coverageZoneOf(rules.coverageZones, geometry)
	const dataAvailability = zone && hailDataAvailability ? inWholeMinutes(hailDataAvailability.coverageZones[zone]) : undefined
	return { dataAvailability, isPending, isError }
}

const HailTimeRangeFields = ({ rules }: { rules: HailReportTypeRules }) => {
	const { trigger, getValues } = useFormContext<HailReportForm>()
	const [timezone] = useHailFormValues(['preferences.timezone'])
	const { dataAvailability } = useCoverageZoneDataAvailability(rules)

	// Shows the errors of a time range kept from before the area changed
	const validateTimeRangeIfSet = useEffectEvent(() => {
		if (getValues('timeRange.start') && getValues('timeRange.end')) void trigger('timeRange')
	})
	useEffect(validateTimeRangeIfSet, [])

	return (
		<>
			<StartEndDateTimeFields<HailReportForm>
				names={{ start: 'timeRange.start', end: 'timeRange.end', errorPath: 'timeRange' }}
				timezone={timezone}
				precision='minutes'
				min={dataAvailability?.start}
				max={dataAvailability?.end}
				disabled={!dataAvailability}
			/>
			<DataAvailabilityAlert rules={rules} />
		</>
	)
}

const DataAvailabilityAlert = ({ rules }: { rules: HailReportTypeRules }) => {
	const [timezone] = useHailFormValues(['preferences.timezone'])
	const formatDate = useFormatDate()
	const { dataAvailability, isPending, isError } = useCoverageZoneDataAvailability(rules)

	if (isError) {
		return (
			<Alert severity='error'>
				<I18n i18nKey={($) => $.reports.form.errorLoadingDataAvailability} />
			</Alert>
		)
	}
	if (isPending) {
		return (
			<Alert severity='info'>
				<I18n i18nKey={($) => $.reports.form.loadingDataAvailability} />
			</Alert>
		)
	}
	if (!dataAvailability) return null

	return (
		<Alert severity='info'>
			<I18n
				i18nKey={($) => $.reports.form.dataAvailability}
				values={{
					startDateTime: formatDate(dataAvailability.start, 'DD.MM.YYYY.HH.mm', timezone),
					endDateTime: formatDate(dataAvailability.end, 'DD.MM.YYYY.HH.mm', timezone),
				}}
			/>
		</Alert>
	)
}
